// Auth module — owns every /auth/* endpoint exposed by the backend.
//
// Backend reference (all under global prefix /api):
//   POST   /auth/register                  (public)
//   POST   /auth/login/apikey              (public)
//   GET    /auth/me                        (JWT)
//   GET    /auth/logout                    (JWT)
//   POST   /auth/refresh                   (public, uses refresh token)
//   GET    /auth/twitter/authorize         (public)
//   GET    /auth/twitter/login             (public, redirect)
//   GET    /auth/twitter/callback          (public, handled by backend)
//   GET    /auth/twitter/link              (JWT)
//   GET    /auth/twitter/link/callback     (JWT, handled by backend)
//   GET    /auth/claim/info                (JWT)
//   GET    /auth/claim/:sessionId          (public)
//   POST   /auth/claim/verify              (public)
//   POST   /auth/provider/apply            (JWT)
//   POST   /auth/chain-connect/nonce       (public, SIWE)
//   POST   /auth/chain-connect/verify      (public, SIWE)
//   POST   /auth/chain-connect/refresh     (public, SIWE)

import { fetchJson } from './client.js';
import { tokens } from './tokens.js';
import { clearAuthBlock } from './client.js';

/**
 * Adapter from backend User to a flat shape the v2 UI expects.
 * Backend User: { id, address, accountType, twitterScreenName, tier, balance, permissions, ... }
 */
export function adaptUser(u) {
  if (!u) return null;
  const handle =
    u.twitterScreenName ||
    (u.address ? (u.address.slice(0, 6) + '…' + u.address.slice(-4)) : ('user-' + String(u.id || '').slice(0, 4)));
  const balance = parseFloat(u.balance || '0') || 0;
  // 服务商 API 收益余额（仅它可提现，独立于充值/赠送 balance）。顶栏据此拆开展示。
  const earningsBalance = parseFloat(u.earningsBalance || '0') || 0;
  return {
    handle,
    accountType: u.accountType || 'VIRTUAL',
    balance,
    earningsBalance,
    canProvideApis: !!u.canProvideApis,
    avatar: null,
    referralCode: u.referralCode || null,
    referredBy: u.referredBy || null, // { referralCode, twitterScreenName, twitterName }
    // Email binding. Backend only writes `email` after the code is verified,
    // so a non-null email == bound & verified (no half-bound state).
    email: u.email || null,
    emailVerified: !!u.email,
    emailVerifiedAt: u.emailVerifiedAt || null,
    _raw: u,
  };
}

// ---------- Quick start (instant register, returns one-time API key) ----------
export async function register(referralCode) {
  const r = await fetchJson('/auth/register', {
    method: 'POST',
    auth: 'none',
    body: { referralCode },
  });
  // backend returns { apiKey, referralCode, bindUrl, user }
  return r.data;
}

// ---------- API-key login ----------
// These endpoints write HttpOnly cookies too. Serialize them so an older login
// response cannot arrive after a newer login or logout and replace its cookies.
let pendingSessionWrite = Promise.resolve();
function writeSession(operation) {
  const pending = pendingSessionWrite.then(operation);
  pendingSessionWrite = pending.catch(() => {});
  return pending;
}

async function sessionRequest(path, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 30_000);
  try {
    const result = await fetchJson(path, { ...options, signal: controller.signal });
    // fetchJson also parses the body; an aborted body must not look successful.
    if (controller.signal.aborted) throw controller.signal.reason;
    return result;
  } catch (error) {
    if (controller.signal.aborted) {
      const timeout = new Error('Authentication request timed out. Please retry.');
      timeout.name = 'TimeoutError';
      throw timeout;
    }
    throw error;
  } finally { clearTimeout(timer); }
}

export function loginWithApiKey(apiKey, { isCurrent = () => true } = {}) {
  return writeSession(async () => {
    const assertCurrent = () => {
      if (!isCurrent()) throw new DOMException('Login was superseded', 'AbortError');
    };
    assertCurrent();
    let r;
    try {
      r = await sessionRequest('/auth/login/apikey', {
        method: 'POST', auth: 'none', body: { apiKey },
      });
    } catch (error) { assertCurrent(); throw error; }
    assertCurrent();
    const { accessToken, refreshToken, user } = r.data || {};
    if (accessToken) {
      tokens.set(accessToken, refreshToken);
      clearAuthBlock();
    }
    return { user, accessToken, refreshToken };
  });
}

// ---------- Twitter OAuth ----------
export async function getTwitterAuthorizeUrl(referralCode) {
  const q = referralCode ? `?referralCode=${encodeURIComponent(referralCode)}` : '';
  const r = await fetchJson('/auth/twitter/authorize' + q, { auth: 'none' });
  return r.data; // { url, state }
}

// Twitter linking (existing user binds Twitter to upgrade)
export async function getTwitterLinkUrl() {
  const r = await fetchJson('/auth/twitter/link', { auth: 'always' });
  return r.data; // { url, state }
}

// ---------- SIWE (chain-connect) ----------
export async function getNonce(address) {
  const r = await fetchJson('/auth/chain-connect/nonce', {
    method: 'POST',
    auth: 'none',
    body: { address },
  });
  return r.data; // { nonce }
}

export async function verifySignature(message, signature) {
  const r = await fetchJson('/auth/chain-connect/verify', {
    method: 'POST',
    auth: 'none',
    body: { message, signature },
  });
  const { accessToken, refreshToken, user } = r.data || {};
  if (accessToken) {
    tokens.set(accessToken, refreshToken);
    clearAuthBlock();
  }
  return { user, accessToken, refreshToken };
}

// ---------- Current user ----------
export async function getMe({ signal } = {}) {
  const r = await fetchJson('/auth/me', { auth: 'always', signal });
  return r.data;
}

// ---------- Logout ----------
export function logout() {
  const access = tokens.getAccess();
  const refresh = tokens.getRefresh();
  tokens.clear();
  clearAuthBlock();
  return writeSession(async () => {
    try {
      const revoke = (token) => sessionRequest('/auth/logout', {
        auth: 'none', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      try { await revoke(access); }
      catch (error) {
        if (error.status !== 401) throw error;
        // Preserve expired-access-token revocation without letting the shared
        // refresh coordinator restore local credentials during logout.
        const renewed = await sessionRequest('/auth/refresh', {
          method: 'POST', auth: 'none', body: refresh ? { refreshToken: refresh } : {},
        });
        if (renewed.data?.accessToken) await revoke(renewed.data.accessToken);
      }
    } catch { /* Local logout still succeeds if server revocation fails. */ }
    finally { tokens.clear(); clearAuthBlock(); }
  });
}

// ---------- Provider application ----------
export async function applyProvider(email, note) {
  const r = await fetchJson('/auth/provider/apply', {
    method: 'POST',
    auth: 'always',
    body: { email, note },
  });
  return r.data;
}

// Aggregate export — keeps call sites readable (auth.loginWithApiKey vs loginWithApiKey)
export const auth = {
  register, loginWithApiKey,
  getTwitterAuthorizeUrl, getTwitterLinkUrl,
  getNonce, verifySignature,
  getMe, logout,
  applyProvider,
  adaptUser,
};

export default auth;
